const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export interface ApiError {
    message: string;
    detail?: string;
    status?: number;
}

export class ApiClient {
    private baseURL: string;
    private refreshing: Promise<boolean> | null = null;

    constructor(baseURL: string = API_BASE_URL) {
        this.baseURL = baseURL;
    }

    private async tryRefresh(): Promise<boolean> {
        // Évite plusieurs refreshs simultanés
        if (this.refreshing) return this.refreshing;
        this.refreshing = (async () => {
            try {
                const response = await fetch(`${this.baseURL}/token/refresh`, {
                    method: 'POST',
                    credentials: 'include', // envoie le cookie httpOnly
                });
                if (!response.ok) return false;
                const data = await response.json();
                localStorage.setItem('access_token', data.access_token);
                return true;
            } catch {
                return false;
            } finally {
                this.refreshing = null;
            }
        })();
        return this.refreshing;
    }

    private async request<T>(
        endpoint: string,
        options: RequestInit = {}
    ): Promise<T> {
        const url = `${this.baseURL}${endpoint}`;
        const token = localStorage.getItem('access_token');

        const config: RequestInit = {
            ...options,
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                ...(token && {Authorization: `Bearer ${token}`}),
                ...options.headers,
            },
        };

        const response = await fetch(url, config);

        if (response.status === 401 && endpoint !== '/token/refresh') {
            const refreshed = await this.tryRefresh();
            if (refreshed) {
                // Rejouer la requête avec le nouveau token
                const newToken = localStorage.getItem('access_token');
                const retryConfig: RequestInit = {
                    ...config,
                    headers: {
                        ...config.headers,
                        Authorization: `Bearer ${newToken}`,
                    },
                };
                const retryResponse = await fetch(url, retryConfig);
                if (!retryResponse.ok) {
                    const errorData = await retryResponse.json().catch(() => ({}));
                    throw new Error(errorData.detail || `HTTP error! status: ${retryResponse.status}`);
                }
                return retryResponse.json();
            }
            // Refresh échoué — déconnexion
            localStorage.removeItem('access_token');
            window.dispatchEvent(new Event('auth:unauthorized'));
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.detail || 'Session expirée');
        }

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
        }

        return response.json();
    }

    async get<T>(endpoint: string): Promise<T> {
        return this.request<T>(endpoint, {method: 'GET'});
    }

    async post<T>(endpoint: string, data?: unknown): Promise<T> {
        return this.request<T>(endpoint, {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    async postForm<T>(endpoint: string, formData: FormData): Promise<T> {
        const url = `${this.baseURL}${endpoint}`;
        const token = localStorage.getItem('access_token');

        const doRequest = (t: string | null) => fetch(url, {
            method: 'POST',
            body: formData,
            credentials: 'include',
            headers: {
                ...(t && {Authorization: `Bearer ${t}`}),
                // Ne pas définir Content-Type pour FormData, le navigateur le fait automatiquement
            },
        });

        let response = await doRequest(token);

        if (response.status === 401) {
            const refreshed = await this.tryRefresh();
            if (refreshed) {
                response = await doRequest(localStorage.getItem('access_token'));
            } else {
                localStorage.removeItem('access_token');
                window.dispatchEvent(new Event('auth:unauthorized'));
            }
        }

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
        }

        return response.json();
    }

    async put<T>(endpoint: string, data?: unknown): Promise<T> {
        return this.request<T>(endpoint, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    async patch<T>(endpoint: string, data?: unknown): Promise<T> {
        return this.request<T>(endpoint, {
            method: 'PATCH',
            body: JSON.stringify(data),
        });
    }

    async delete<T>(endpoint: string): Promise<T> {
        return this.request<T>(endpoint, {method: 'DELETE'});
    }
}

export const apiClient = new ApiClient();
